/**
 * Shared folder processing utilities for recursive directory traversal
 * Supports both drag-and-drop FileSystem API and manual selection File System Access API
 */

/**
 * Recursively process a directory entry from drag-and-drop (FileSystem API)
 */
export const processDirectoryEntry = async (entry: any): Promise<File[]> => {
  const files: File[] = []

  if (entry.isFile) {
    return new Promise((resolve) => {
      entry.file((file: File) => {
        // Add the webkitRelativePath to maintain folder structure
        Object.defineProperty(file, 'webkitRelativePath', {
          value: entry.fullPath.slice(1), // Remove leading slash
          writable: false,
        })
        resolve([file])
      })
    })
  } else if (entry.isDirectory) {
    const reader = entry.createReader()
    const entries = await new Promise<any[]>((resolve) => {
      reader.readEntries(resolve)
    })

    for (const childEntry of entries) {
      const childFiles = await processDirectoryEntry(childEntry)
      files.push(...childFiles)
    }
  }

  return files
}

/**
 * Recursively process a directory handle from manual selection (File System Access API)
 */
export const processDirectoryHandle = async (handle: any, pathPrefix = ''): Promise<File[]> => {
  const files: File[] = []

  for await (const entry of handle.values()) {
    const entryPath = pathPrefix ? `${pathPrefix}/${entry.name}` : entry.name

    if (entry.kind === 'file') {
      const file = await entry.getFile()
      // Add webkitRelativePath for compatibility with upload API
      Object.defineProperty(file, 'webkitRelativePath', {
        value: entryPath,
        writable: false,
      })
      files.push(file)
    } else if (entry.kind === 'directory') {
      const childFiles = await processDirectoryHandle(entry, entryPath)
      files.push(...childFiles)
    }
  }

  return files
}

/**
 * Create a FileList-like object from an array of files
 */
export const createFileList = (files: File[]): FileList => {
  return Object.assign(files, {
    length: files.length,
    item: (index: number) => files[index] || null,
  }) as FileList
}

/**
 * Process folder selection using the most appropriate method
 * Tries modern File System Access API first, falls back to traditional input
 */
export const processFolderSelection = async (): Promise<FileList | null> => {
  // Try to use the directory picker API if available (Chrome 86+)
  if ('showDirectoryPicker' in window) {
    try {
      const dirHandle = await (window as any).showDirectoryPicker()
      // Include the root folder name in the path prefix to maintain folder structure
      const allFiles = await processDirectoryHandle(dirHandle, dirHandle.name)
      return createFileList(allFiles)
    } catch (error) {
      console.warn('Directory picker failed, falling back to file input:', error)
    }
  }

  // Fallback: return null to indicate traditional file input should be used
  return null
}
